/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { type ChangeEvent, useEffect, useState, useRef } from 'react'
import { type caregiverChildRelationship, type PortalCaregiverSummary, type lookup, type postError, type location, type race } from 'core/types'
import { type ChildMemberDetails } from 'Pages/CaregiverPortal/Members/ChildDisplay'
import { sendGet, sendPost, sendPostFormData, sendPut } from 'hooks/use-fetch'
import { Avatar, Box, Grid, IconButton, Typography, type SelectChangeEvent, Container, CircularProgress, Button, DialogActions, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { TextFieldWithLabel } from 'Components/TextField'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import FilePicker from 'Components/FilePicker'
import S3Avatar from 'Components/S3Image/S3Avatar'
import { AgeFromDate } from 'core/Utilities'
import { generatePath, useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { CAREGIVER_PORTAL_MEMBERS, CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS } from 'Pages/Routes'
import { childName } from 'Pages/CaregiverPortal/PortalHelpers'
import { type ChildRelationship } from 'Pages/CaregiverPortal/Members/ChildRelationships'
import { useLazyCheckChildDuplicateQuery, type ChildDuplicate, duplicateSource, updateDuplicateInfo, dupeSelector } from 'slices/duplicateChildSlice'
import { DuplicateFound } from 'Pages/CaregiverPortal/Members/DuplicateFound'
import * as AppRoutes from 'Pages/Routes'
import { useAuth } from 'hooks/use-auth'
import { DuplicateRequestReview } from './DuplicateReviewRequest'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'hooks/reduxHooks'
import { useErrorDispatch } from 'ContextProviders/ErrorProvider'
import { AddressCard } from 'Components/Cards/CaregiverPortal/AddressCard'
import { RelationshipCard } from 'Components/Cards/CaregiverPortal/RelationshipCard'
import { AddressChangeType, ChangeAddress, type MemberType } from 'Pages/CaregiverPortal/Members/ChangeAddress'
import { RelationshipEdit } from 'Pages/CaregiverPortal/Members/RelationshipEdit'
import { RaceSelection } from 'Pages/CaregiverPortal/Members/RaceSelection'
import { BackArrow } from 'Assets/SVGs/back-arrow'

const newChild: ChildMemberDetails = {
  id: 0,
  isdid: 0,
  firstName: '',
  lastName: '',
  middleName: '',
  nickname: '',
  suffix: '',
  dateOfBirth: null,
  birthGender: '',
  races: [],
  childRaceList: [],
  tribalAffiliationStatus: null,
  tribalAffiliationID: null,
  languageID: undefined,
  primaryLanguage: '',
  hasIEP: false,
  hasIFSP: false,
  hasEOIS: false,
  fosterChild: false,
  hasSNAPBenefits: false,
  isOnCashAssistance: false,
  isOnSSI: false,
  dueDate: null,
  unborn: false,
  primaryLocationID: null,
  locationIDs: [],
  location: null,
  newLocation: null,
  canEdit: true,
  hasPendingDuplicate: false,
  enrollmentCountByService: [],
  imageKey: null
}

interface ChildAddress {
  id: number
  address: string
  apartment: string
  city: string
  state: string
  zipCode: string
  isHomeless: boolean
  districtID: number | null
  sharedWithOthers: boolean
  sharedChildren: Array<{ id: number, name: string }>
}

interface PendingAddress {
  tempId: number
  address: string
  apartment: string
  city: string
  state: string
  zipCode: string
  isHomeless: boolean
  // only set when picked by hand
  districtID: number | undefined
}

interface PendingModification {
  oldLocationID: number
  changeType: AddressChangeType
  address: string
  apartment: string
  city: string
  state: string
  zipCode: string
  isHomeless: boolean
  // only set when picked by hand
  districtID: number | undefined
  selectedMembers: Array<{ id: number, type: MemberType }>
}

export function ChildEdit (): JSX.Element {
  const { childId } = useParams()
  const auth = useAuth()
  const isNew = useRef(childId === 'new')
  const [isdID, setIsdID] = useState(0)
  const nav = useNavigate()
  const [child, setChild] = useState<ChildMemberDetails>(newChild)
  // name as loaded, so the title doesn't change while the name fields are being edited
  const [initialName, setInitialName] = useState('')
  const [relationships, setRelationships] = useState<ChildRelationship[]>([])
  const [languages, setLanguages] = useState<lookup[]>([])
  const [raceOptions, setRaceOptions] = useState<race[]>([])
  const [addresses, setAddresses] = useState<ChildAddress[]>([])
  const [pendingNewAddresses, setPendingNewAddresses] = useState<PendingAddress[]>([])
  const [pendingModifications, setPendingModifications] = useState<PendingModification[]>([])
  const nextTempAddressId = useRef(-1)
  const nextTempRelationshipId = useRef(-1)
  const [addingAddress, setAddingAddress] = useState(false)
  const [modifyingAddress, setModifyingAddress] = useState<ChildAddress | null>(null)
  const [modifyingPendingAddress, setModifyingPendingAddress] = useState<PendingAddress | null>(null)
  const [modifyingRelationship, setModifyingRelationship] = useState<ChildRelationship | null>(null)
  const [relationshipErrors, setRelationshipErrors] = useState<postError[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [canEditUnborn, setCanEditUnborn] = useState(isNew.current)
  const [submitting, setSubmitting] = useState(false)
  const [captureIEP, setCaptureIEP] = useState(false)
  const [captureIFSP, setCaptureIFSP] = useState(false)
  const [captureEOIS, setCaptureEOIS] = useState(false)
  const [errors, setErrors] = useState<postError[]>([])
  // RaceSelection's Latino or Hispanic answer, kept apart from races so an unanswered (null) toggle can be flagged
  const [isHispanic, setIsHispanic] = useState<boolean | null>(null)
  const [pendingImageName, setPendingImageName] = useState<string | null>(null)
  const childImage = useRef<File | null | undefined>(undefined) // null means the image was removed, undefined means no action was taken
  const errorDispatch = useErrorDispatch()
  const [triggerDupeCheck] = useLazyCheckChildDuplicateQuery()
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false)
  const generalDispatch = useDispatch()
  const dupeData = useAppSelector(dupeSelector)

  useEffect(() => {
    // StrictMode runs this effect twice in development; without this guard the discarded run's GetChildDetails can land
    // after the form is already showing and replace the whole child, undoing an edit (e.g. Cypress answering No to unborn)
    let cancelled = false
    const fetchData = async (): Promise<void> => {
      const { response, error } = await sendGet('/Language/GetLookup?sort=true', {})
      if (cancelled) return
      if (error[0] === '') {
        setLanguages(response)
      }

      const { response: raceResponse } = await sendGet('/Race/GetLookup', {})
      if (cancelled) return
      const loadedRaceOptions: race[] = raceResponse ?? []
      setRaceOptions(loadedRaceOptions)

      if (isNew.current) {
        const { response: isdRsp } = await sendGet('/CaregiverPortal/GetISDSettings?id=', {})
        if (cancelled) return
        if (isdRsp != null) {
          setIsdID(isdRsp.isdid)
          setCaptureIEP(isdRsp.captureIEP)
          setCaptureIFSP(isdRsp.captureIFSP)
          setCaptureEOIS(isdRsp.captureEOIS)
        }

        // get the caregivers and load both the how-related list and the person lives with dropdown list
        const { response: relRsp } = await sendGet('/CaregiverPortal/GetRelatedMembers', {})
        if (cancelled) return
        let firstCaregiverID: number | null = null
        if (relRsp != null) {
          const howRelatedList: ChildRelationship[] = []
          const livesWithList: lookup[] = []
          let selfRelationship: ChildRelationship | null = null
          relRsp.caregivers.forEach((c: PortalCaregiverSummary) => {
            if (firstCaregiverID === null) {
              firstCaregiverID = c.id
            }
            livesWithList.push({
              id: c.id,
              name: `${c.firstName} ${c.lastName}`
            })
            const relationship: ChildRelationship = {
              caregiverID: c.id,
              relationshipID: null,
              financiallyResponsibleFor: null,
              livesWithChild: false,
              caregiverName: `${c.firstName} ${c.lastName}`,
              // the logged-in caregiver is always linked to a child they add - start them as a pending relationship
              // (temp id) rather than "available to add", so it's listed up front and can't be removed
              id: c.isSelf ? nextTempRelationshipId.current-- : null,
              childID: 0,
              relationshipName: null,
              isSelf: c.isSelf
            }
            if (c.isSelf) {
              selfRelationship = relationship
            }
            howRelatedList.push(relationship)
          })
          setRelationships(howRelatedList)
          // start in edit mode on their relationship - Save stays disabled until it's filled in and confirmed
          setModifyingRelationship(selfRelationship)
        }
        handleValueChange('livesWithCaregiverID', firstCaregiverID ?? 0)

        setIsLoading(false)
      } else {
        const { response: childRsp, success } = await sendGet(`/CaregiverPortal/GetChildDetails/${childId ?? '0'}`, {})
        if (cancelled) return
        if (success) {
          const childRaceList: race[] = (childRsp.races ?? []).map((id: number) => {
            const match = loadedRaceOptions.find(r => r.id === id)
            return { id, name: match?.name ?? '', codePosition: match?.codePosition ?? 0 }
          })
          // an unborn child's stored date of birth is a DateTime.MinValue placeholder - start with none, so recording
          // the birth requires one to be entered
          const dateOfBirth = childRsp.unborn === true ? null : childRsp.dateOfBirth
          setChild({ ...childRsp, childRaceList, dateOfBirth })
          const { firstName, lastName }: ChildMemberDetails = childRsp
          setInitialName(`${firstName ?? ''} ${lastName ?? ''}`.trim())
          setCanEditUnborn(childRsp.unborn === true)
        } else {
          toast.error('Failed to load child details')
        }
        await fetchRelationships()
        if (cancelled) return
      }

      const { response: relAdr } = await sendGet(`/CaregiverPortal/GetCaregiverAddresses?childID=${(isNew.current ? '' : childId ?? '')}`, {})
      if (cancelled) return
      if (relAdr != null) {
        setAddresses(relAdr)
        if (isNew.current && relAdr.length > 0) {
          const addressIds = relAdr.map((a: ChildAddress) => a.id)
          setChild(prev => ({ ...prev, locationIDs: addressIds, primaryLocationID: prev.primaryLocationID ?? addressIds[0] }))
        }
      }
    }
    void fetchData()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!isNew.current && child.id !== 0) {
        const { response: isdRsp } = await sendGet(`/CaregiverPortal/GetISDSettings?id=${child.isdid ?? ''}`, {})
        if (isdRsp != null) {
          setIsdID(isdRsp.isdid)
          setCaptureIEP(isdRsp.captureIEP)
          setCaptureIFSP(isdRsp.captureIFSP)
          setCaptureEOIS(isdRsp.captureEOIS)
        }
        setIsLoading(false)
      }
    }
    void fetchData()
  }, [child])

  const genderOptions: Array<{ id: string, name: string }> = [
    { id: 'M', name: 'Male' },
    { id: 'F', name: 'Female' }
  ]

  // updates build on the latest state, not this render's child - RaceSelection reports its values from an effect, and
  // a stale copy applied after a quick edit (e.g. Cypress answering the unborn question) would undo that edit
  const handleChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const { name, value } = e.target
    setChild(prev => ({ ...prev, [name]: value }))
  }

  const handleValueChange = (name: string, newValue: any): void => {
    setChild(prev => ({ ...prev, [name]: newValue }))
  }

  const handleSelectChange = async (e: SelectChangeEvent<any | null>): Promise<void> => {
    const { name, value } = e.target
    setChild(prev => ({ ...prev, [name]: value }))
  }

  const handleRaceListChange = (raceList: race[], hispanic: boolean | null): void => {
    setIsHispanic(hispanic)
    // Hispanic/Latino has no field of its own - it stays race ID 4 in races/childRaceList, same as everywhere else it's stored
    const races = raceList.map(r => r.id)
    const childRaceList = [...raceList]
    if (hispanic === true) {
      const ethnicityRace = raceOptions.find(r => r.id === 4)
      races.push(4)
      childRaceList.push(ethnicityRace ?? { id: 4, name: '', codePosition: 0 })
    }
    setChild(prev => ({ ...prev, races, childRaceList }))
  }

  const fetchRelationships = async (): Promise<void> => {
    const { response, success } = await sendGet(`/CaregiverPortal/GetChildRelationships/${childId ?? '0'}`, {})
    if (success) {
      setRelationships(response)
    }
  }

  const handleRelationshipSubmit = (data: ChildRelationship): void => {
    // local only - not saved server-side until the child record is saved
    setRelationshipErrors([])
    const isNewlyConfigured = data.id === null
    const finalized: ChildRelationship = isNewlyConfigured ? { ...data, id: nextTempRelationshipId.current-- } : data
    const isMatch = (r: ChildRelationship): boolean => isNewlyConfigured ? (r.id === null && r.caregiverID === data.caregiverID) : r.id === data.id
    if (relationships.some(isMatch)) {
      setRelationships(relationships.map(r => (isMatch(r) ? finalized : r)))
    } else {
      // a caregiver added via "+ Add New Caregiver" from within the relationship editor - not previously in the list
      setRelationships([...relationships, finalized])
    }
    setModifyingRelationship(null)
  }

  const handleDeleteRelationship = (caregiverID: number): void => {
    // local only - reverts a not-yet-saved pending relationship back to "available to add"
    setRelationships(relationships.map(r => r.caregiverID === caregiverID
      ? { ...r, id: null, relationshipID: null, financiallyResponsibleFor: null, livesWithChild: false }
      : r))
    setModifyingRelationship(null)
  }

  const returnToDetails = (): void => {
    let route: string
    if (isNew.current) {
      route = generatePath(CAREGIVER_PORTAL_MEMBERS)
    } else {
      route = generatePath(CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, { childId: childId ?? '' })
    }
    nav(route)
  }

  const fieldHasError = (name: string): boolean => errors.some(e => e.field === name)
  const fieldError = (name: string): string | undefined => errors.find(e => e.field === name)?.error
  // errors for these fields are shown beneath the field itself; anything else falls back to the list at the top
  const inlineErrorFields = ['firstName', 'lastName', 'dateOfBirth', 'dueDate', 'birthGender', 'races', 'isHispanic', 'addresses']
  const summaryErrors = errors.filter(e => !inlineErrorFields.includes(e.field))

  // once errors render, bring the first one on the page into view (summary list, field outline, or error caption - whichever comes first)
  const formRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (errors.length === 0) return
    const firstError = formRef.current?.querySelector('[data-error-summary], .Mui-error, [data-testid$="Error"]')
    if (firstError != null) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo(0, 0)
    }
  }, [errors])

  const availableRelationships = relationships.filter(r => r.id === null)

  const hasUnsavedSubEdits = modifyingAddress != null || modifyingPendingAddress != null || modifyingRelationship != null ||
    addingAddress

  // one address editor at a time - each binds the address autosuggest to the same input id
  const closeAddressEditors = (): void => {
    setAddingAddress(false)
    setModifyingAddress(null)
    setModifyingPendingAddress(null)
  }

  const startAddingAddress = (): void => {
    closeAddressEditors()
    setAddingAddress(true)
  }

  const startModifyingAddress = (a: ChildAddress): void => {
    closeAddressEditors()
    setModifyingAddress(a)
  }

  const startModifyingPendingAddress = (p: PendingAddress): void => {
    closeAddressEditors()
    setModifyingPendingAddress(p)
  }

  const handleSetPrimaryAddress = (locationID: number): void => {
    // local only - not persisted until the child record is saved
    setChild(prev => ({ ...prev, primaryLocationID: locationID }))
  }

  const handleRemoveAddress = (locationID: number): void => {
    // local only - UpdateChildDetails rebuilds the child's addresses from locationIDs on save,
    // so omitting the id here is all that's needed; nothing is unlinked server-side until then
    if (locationID < 0) {
      setPendingNewAddresses(pendingNewAddresses.filter(a => a.tempId !== locationID))
    } else {
      setPendingModifications(pendingModifications.filter(m => m.oldLocationID !== locationID))
    }
    setChild(prev => ({ ...prev, locationIDs: prev.locationIDs.filter(l => l !== locationID) }))
  }

  const handleAddAddressSubmit = (newLocation: location): void => {
    // local only - the address isn't created server-side until the child record is saved
    const tempId = nextTempAddressId.current
    nextTempAddressId.current -= 1
    setPendingNewAddresses([...pendingNewAddresses, {
      tempId,
      address: newLocation.address,
      apartment: newLocation.apartment,
      city: newLocation.city,
      state: newLocation.state,
      zipCode: newLocation.zipCode,
      isHomeless: newLocation.isHomeless ?? false,
      districtID: newLocation.districtID
    }])
    setChild(prev => ({ ...prev, locationIDs: [...prev.locationIDs, tempId], primaryLocationID: prev.primaryLocationID ?? tempId }))
    setAddingAddress(false)
  }

  const handleModifyPendingAddressSubmit = (newLocation: location): void => {
    if (modifyingPendingAddress == null) return
    setPendingNewAddresses(pendingNewAddresses.map(p => p.tempId === modifyingPendingAddress.tempId
      ? { ...p, address: newLocation.address, apartment: newLocation.apartment, city: newLocation.city, state: newLocation.state, zipCode: newLocation.zipCode, isHomeless: newLocation.isHomeless ?? false, districtID: newLocation.districtID }
      : p))
    setModifyingPendingAddress(null)
  }

  const handleModifyAddressSubmit = (newLocation: location, changeType: AddressChangeType, selectedMembers: Array<{ id: number, type: MemberType }>): void => {
    // local only - not saved server-side until the child record is saved
    if (modifyingAddress == null) return
    const modification: PendingModification = {
      oldLocationID: modifyingAddress.id,
      changeType,
      address: newLocation.address,
      apartment: newLocation.apartment,
      city: newLocation.city,
      state: newLocation.state,
      zipCode: newLocation.zipCode,
      isHomeless: newLocation.isHomeless ?? false,
      districtID: newLocation.districtID,
      selectedMembers
    }
    setPendingModifications([...pendingModifications.filter(m => m.oldLocationID !== modifyingAddress.id), modification])
    setModifyingAddress(null)
  }

  const setChildImage = (files: FileList | null): void => {
    if (files?.[0] != null) {
      childImage.current = files[0]
      setPendingImageName(files[0].name)
    }
  }

  const removeChildImage = (): void => {
    childImage.current = null
    setPendingImageName(null)
    setChild(prev => ({ ...prev, imageKey: null }))
  }

  const cancelPendingImage = (): void => {
    childImage.current = undefined
    setPendingImageName(null)
  }

  const saveChildImage = async (savedChildId: number): Promise<void> => {
    if (childImage.current !== undefined) {
      const fileData = new FormData()
      fileData.append('id', savedChildId.toString())
      if (childImage.current === null) {
        await sendPostFormData('/CaregiverPortal/RemoveChildImage', fileData)
      } else {
        fileData.append('file', childImage.current)
        await sendPostFormData('/CaregiverPortal/UploadChildImage', fileData)
      }
    }
  }

  const validateRequiredFields = (): postError[] => {
    const validationErrors: postError[] = []
    if (!child.unborn && (child.firstName == null || child.firstName.trim() === '')) {
      validationErrors.push({ field: 'firstName', error: 'First Name is required' })
    }
    if (child.lastName == null || child.lastName.trim() === '') {
      validationErrors.push({ field: 'lastName', error: 'Last Name is required' })
    }
    if (!child.unborn && child.dateOfBirth == null) {
      validationErrors.push({ field: 'dateOfBirth', error: 'Date of Birth is required' })
    }
    if (child.unborn && child.dueDate == null) {
      validationErrors.push({ field: 'dueDate', error: 'Due Date is required' })
    }
    if (!child.unborn && (child.birthGender == null || child.birthGender === '')) {
      validationErrors.push({ field: 'birthGender', error: 'Assigned Gender at Birth is required' })
    }
    if (!child.unborn && child.races.filter(r => r !== 4).length === 0) {
      validationErrors.push({ field: 'races', error: 'Race is required' })
    }
    // client-side only: the saved races list can't tell "No" from "never answered", so the server can't check this
    if (!child.unborn && isHispanic === null) {
      validationErrors.push({ field: 'isHispanic', error: 'Latino or Hispanic is required' })
    }
    return validationErrors
  }

  const resolvePendingAddresses = async (): Promise<{ locationIDs: number[], primaryLocationID: number | null } | null> => {
    let locationIDs = [...child.locationIDs]
    let primaryLocationID = child.primaryLocationID
    for (const pending of pendingNewAddresses) {
      const data = { personID: child.id, changeType: AddressChangeType.SinglePerson, address: pending.address, apartment: pending.apartment, city: pending.city, state: pending.state, zipCode: pending.zipCode, isHomeless: pending.isHomeless, districtID: pending.districtID ?? null, selectedMembers: [] }
      const { response } = await sendPost('/CaregiverPortal/AddChildAddress', data)
      if (response?.success !== true || response?.newRecordID == null) {
        toast.error(response?.errors?.[0]?.error ?? 'Failed to save a new address')
        return null
      }
      const realId: number = response.newRecordID
      locationIDs = locationIDs.map(id => (id === pending.tempId ? realId : id))
      if (primaryLocationID === pending.tempId) {
        primaryLocationID = realId
      }
    }

    for (const mod of pendingModifications) {
      const data = { personID: child.id, oldLocationID: mod.oldLocationID, changeType: mod.changeType, address: mod.address, apartment: mod.apartment, city: mod.city, state: mod.state, zipCode: mod.zipCode, isHomeless: mod.isHomeless, districtID: mod.districtID ?? null, selectedMembers: mod.selectedMembers }
      const { response } = await sendPut('/CaregiverPortal/UpdateChildAddress', data)
      if (response?.success !== true || response?.newRecordID == null) {
        toast.error(response?.errors?.[0]?.error ?? 'Failed to save an address change')
        return null
      }
      const realId: number = response.newRecordID
      locationIDs = locationIDs.map(id => (id === mod.oldLocationID ? realId : id))
      if (primaryLocationID === mod.oldLocationID) {
        primaryLocationID = realId
      }
    }

    return { locationIDs, primaryLocationID }
  }

  const handleSubmit = async (): Promise<void> => {
    if (!submitting) {
      setSubmitting(true)
      errorDispatch({ type: 'clear', errors: [] })

      const clientErrors = validateRequiredFields()
      if (clientErrors.length > 0) {
        setErrors(clientErrors)
        setSubmitting(false)
        return
      }

      let rsp: { success: boolean, errors: postError[], newRecordID: number | null }
      if (isNew.current) {
        if (relationships.find(r => r.isSelf === true)?.relationshipID == null) {
          toast.error('Please select your relationship to this child')
          setSubmitting(false)
          return
        }

        const childDupeRecord = translateChildMemberValues(child, duplicateSource.CaregiverPortal)
        const duplicateCheckResult = await triggerDupeCheck(childDupeRecord)
        if (duplicateCheckResult.isSuccess && duplicateCheckResult.data.length > 0) {
          const firstMatch = duplicateCheckResult.data[0]
          generalDispatch(updateDuplicateInfo({ ...childDupeRecord, canAccess: firstMatch.canAccess }))
          setShowDuplicateAlert(true)
          return
        }
        const pendingNew = pendingNewAddresses[0]
        const childToSubmit = {
          ...child,
          locationIDs: child.locationIDs.filter(id => id > 0),
          primaryLocationID: (child.primaryLocationID != null && child.primaryLocationID > 0) ? child.primaryLocationID : null,
          newLocation: pendingNew != null
            ? { address: pendingNew.address, apartment: pendingNew.apartment, city: pendingNew.city, state: pendingNew.state, zipCode: pendingNew.zipCode, isHomeless: pendingNew.isHomeless, districtID: pendingNew.districtID ?? null }
            : null,
          // saved with the child - the server won't accept relationships to a child nobody has access to yet
          relationships: relationships.map(r => ({ ...r, id: null, childID: 0 }))
        }
        const result = await sendPost('/CaregiverPortal/InsertChild', childToSubmit)
        rsp = result.response
      } else {
        let childToSave = child
        if (pendingNewAddresses.length > 0 || pendingModifications.length > 0) {
          const resolved = await resolvePendingAddresses()
          if (resolved == null) {
            setSubmitting(false)
            return
          }
          childToSave = { ...child, locationIDs: resolved.locationIDs, primaryLocationID: resolved.primaryLocationID }
          setPendingNewAddresses([])
          setPendingModifications([])
          setChild(childToSave)
        }
        childToSave.location = null
        const result = await sendPut('/CaregiverPortal/UpdateChildDetails', childToSave)
        rsp = result.response
        if (result.success) {
          const relationshipsToSubmit = relationships.map(r => (r.id != null && r.id < 0) ? { ...r, id: null } : r)
          await sendPost('/CaregiverPortal/UpdateChildRelationships', relationshipsToSubmit)
        }
      }

      if (rsp.success) {
        const savedChildId = isNew.current ? rsp.newRecordID : child.id
        if (savedChildId != null) {
          await saveChildImage(savedChildId)
        }
        if (isNew.current) {
          const route = generatePath(CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, { childId: rsp?.newRecordID?.toString() ?? '' })
          nav(route)
        } else {
          returnToDetails()
        }
      } else {
        setSubmitting(false)
        setErrors(rsp.errors)
      }
    }
  }

  const translateChildMemberValues = (data: ChildMemberDetails, source: duplicateSource): ChildDuplicate => {
    let location: ChildAddress | undefined
    if (data.primaryLocationID != null) {
      location = addresses.find(a => a.id === data.primaryLocationID)
    } else if (data.locationIDs.length > 0) {
      location = addresses.find(a => a.id === data.locationIDs[0])
    }
    const idNum = childId === 'new' ? 0 : Number.parseInt(childId ?? '0')
    const result: ChildDuplicate = {
      childId: idNum === 0 ? undefined : idNum,
      firstName: data.firstName,
      lastName: data.lastName,
      middleName: data.middleName,
      suffix: data.suffix,
      birthGender: data.birthGender ?? '',
      dateOfBirth: data.dateOfBirth?.toDateString() ?? '',
      languageID: data.languageID?.toString() ?? '',
      race: data.childRaceList.map(r => r.name).join(','),
      tribalAffiliationStatus: data.tribalAffiliationStatus ?? undefined,
      tribalAffiliationID: data.tribalAffiliationID ?? undefined,
      isdId: data.isdid,
      address: (location?.address) ?? '',
      apartment: (location?.apartment) ?? '',
      city: (location?.city) ?? '',
      state: (location?.state) ?? '',
      zipCode: (location?.zipCode) ?? '',
      matchedValues: [],
      requestingUserID: (auth.user?.id ?? 0).toString(),
      requestingUserIsCaregiver: true,
      source,
      canAccess: data.canEdit,
      // relationships also holds an unconfigured row for every other caregiver in the network ("available to add");
      // those have no relationship type, and sending one as NaN/null fails deserialization into the server's int RelationshipTypeID
      caregiverRelationships: relationships.filter(r => r.relationshipID != null).map(r => {
        return { caregiverID: r.caregiverID, relationshipID: Number(r.relationshipID), financiallyResponsible: r.financiallyResponsibleFor } as caregiverChildRelationship
      })
    }
    return result
  }

  if (isLoading) {
    return <CircularProgress />
  } else if (showDuplicateAlert) {
    const editChild = generatePath(AppRoutes.CAREGIVER_PORTAL_MEMBERS)
    const val = (dupeData?.isdId != null && dupeData?.isdId !== 0) ? dupeData?.isdId : (isdID ?? 0)
    return <>

      <DuplicateFound isdId={val} backPath={editChild} type='provider'/>
      <DuplicateRequestReview isIntakeForm={false} userId={auth.user?.id ?? 0} userType='caregiver'/>

    </>
  } else {
    return <Container sx={{ mb: '24px' }}>
      <Box sx={{ mt: '24px', mb: '24px', display: 'flex', alignItems: 'center' }}>
        <Box onClick={returnToDetails} data-testid='backArrow' sx={{ cursor: 'pointer', display: 'flex' }}>
          <BackArrow />
        </Box>
        <Typography variant='h5' sx={{ fontWeight: 600, ml: '5px' }} component='div'>
          {isNew.current ? 'New Child' : `Edit Child Details${initialName !== '' ? ` - ${initialName}` : ''}`}
        </Typography>
      </Box>

      {canEditUnborn && (
        <Box sx={{ mb: '20px' }}>
          <Typography variant='subtitle1' gutterBottom>Is this an unborn child?</Typography>
          <ToggleButtonGroup
            className='yes-no-button-group'
            value={child.unborn ? 1 : 0}
            exclusive
            onChange={(event, value) => { if (value != null) handleValueChange('unborn', value === 1) }}
          >
            <ToggleButton value={1}>Yes</ToggleButton>
            <ToggleButton value={0}>No</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box ref={formRef}>
        {summaryErrors.length > 0 &&
          <ul data-error-summary>
            {summaryErrors.map((err) => (
              <li key={err.field} style={{ color: 'red', padding: '10px 0 0' }}>
                {err.error}
              </li>
            ))}
          </ul>
        }
        <Grid container spacing={1} columns={6}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '8px' }}>
              {child.imageKey != null && child.imageKey !== ''
                ? <S3Avatar imageKey={child.imageKey} height='32px' width='32px' />
                : <Avatar sx={{ height: '32px', width: '32px' }} />}
              <FilePicker label='Change Image' buttonVariant='text' name='file' onSelect={(e) => { setChildImage(e.target.files) }} />
              {(child.imageKey != null && child.imageKey !== '') && (
                <Button data-testid='removeImage' size='small' onClick={removeChildImage}>Remove</Button>
              )}
            </Box>
            {pendingImageName != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant='caption'>{pendingImageName}</Typography>
                <IconButton data-testid='cancelPendingImage' size='small' onClick={cancelPendingImage} aria-label='Remove selected image'>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
            )}
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='First Name' required={!child.unborn} showRequiredText={child.unborn || fieldHasError('firstName')} error={fieldHasError('firstName')} helperText={fieldError('firstName')} data-testid="firstName" name='firstName' maxLength={50} value={child?.firstName ?? ''} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Middle Name' showRequiredText={true} data-testid="middleName" name='middleName' maxLength={50} value={child.middleName} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Last Name' required={true} showRequiredText={fieldHasError('lastName')} error={fieldHasError('lastName')} helperText={fieldError('lastName')} data-testid="lastName" name='lastName' maxLength={50} value={child.lastName} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextFieldWithLabel label='Suffix' showRequiredText={true} data-testid="suffix" name='suffix' maxLength={10} value={child.suffix} onChange={handleChange} />
          </Grid>
          {!child.unborn && (
            <Grid item xs={6}>
              <DatePickerWithLabel label='Date of Birth' required={true} showRequiredText={fieldHasError('dateOfBirth')} error={fieldHasError('dateOfBirth')} helperText={fieldError('dateOfBirth')} onChange={(newValue) => { handleValueChange('dateOfBirth', newValue) }} value={child?.dateOfBirth ?? null} name='dateOfBirth' />
            </Grid>
          )}
          {child.unborn && (
            <Grid item xs={6}>
              <DatePickerWithLabel label='Due Date' required={true} showRequiredText={fieldHasError('dueDate')} error={fieldHasError('dueDate')} helperText={fieldError('dueDate')} onChange={(newValue) => { handleValueChange('dueDate', newValue) }} value={child?.dueDate ?? null} name='dueDate' />
            </Grid>
          )}
          {!child.unborn && (
            <Grid item xs={6}>
              <SelectWithLabel
                name="birthGender"
                label="Assigned Gender at Birth"
                width="100%"
                value={child.birthGender}
                required={true}
                showRequiredText={fieldHasError('birthGender')}
                error={fieldHasError('birthGender')}
                includeNoneOption={true}
                options={genderOptions}
                onChange={handleSelectChange}
              />
              {fieldHasError('birthGender') && (
                <Typography variant='caption' color='error' display='block' data-testid='birthGenderError' sx={{ mt: '-12px' }}>{fieldError('birthGender')}</Typography>
              )}
            </Grid>
          )}
          {!child.unborn && (
            <Grid item xs={6}>
              <SelectWithLabel
                name="languageID"
                label="Primary Language (if not English)"
                width="100%"
                includeNoneOption={true}
                value={child.languageID}
                options={languages}
                onChange={handleSelectChange}
              />
            </Grid>
          )}

          {!child.unborn && (
            <Grid item xs={12}>
              <RaceSelection
                raceList={child.childRaceList.filter(r => r.id !== 4)}
                // no races at all means ethnicity was never answered (new child, or a record predating the requirement) - leave both options unselected
                isHispanic={child.races.length === 0 ? null : child.races.includes(4)}
                setRaceList={handleRaceListChange}
                tribalAffiliationStatus={child.tribalAffiliationStatus ?? false}
                tribalAffiliationID={child.tribalAffiliationID}
                setTribeStatus={(checked, tribeId) => {
                  setChild(prev => ({ ...prev, tribalAffiliationStatus: checked, tribalAffiliationID: tribeId }))
                }}
                raceError={fieldError('races')}
                ethnicityError={fieldError('isHispanic')}
              />
            </Grid>
          )}

          {!child.unborn && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>Additional Information</Typography>
                <Typography sx={{ color: '#969696', fontSize: '12px' }}>Optional</Typography>
              </Box>
              <Box sx={{
                backgroundColor: '#ECEDEF',
                borderRadius: '10px',
                p: '12px',
                display: 'flex',
                flexDirection: 'column',
                mt: '8px',
                '& .MuiCheckbox-root': { padding: '4px' },
                '& .MuiListItemIcon-root': { minWidth: '32px', height: 'auto' }
              }}
              >
                {captureIEP && child.dateOfBirth !== null && AgeFromDate(child.dateOfBirth) >= 3 &&
                  <CheckBoxWithLabel label='Has an Individualized Education Plan (IEP)' edge='start' name='hasIEP' value={child.hasIEP} setValue={(val) => { handleValueChange('hasIEP', val) }} />
                }
                {captureIFSP && child.dateOfBirth !== null && AgeFromDate(child.dateOfBirth) < 3 &&
                  <CheckBoxWithLabel label='Has an Individualized Family Service Plan (IFSP)' edge='start' name='hasIFSP' value={child.hasIFSP} setValue={(val) => { handleValueChange('hasIFSP', val) }} />
                }
                {captureEOIS && child.dateOfBirth !== null && AgeFromDate(child.dateOfBirth) >= 3 &&
                  <CheckBoxWithLabel label='Has an Early On Intervening Service Plan (EOIS)' edge='start' name='hasEOIS' value={child.hasEOIS} setValue={(val) => { handleValueChange('hasEOIS', val) }} />
                }
                <CheckBoxWithLabel label='Foster Child' edge='start' name='fosterChild' value={child.fosterChild} setValue={(val) => { handleValueChange('fosterChild', val) }} />
                <CheckBoxWithLabel label='SNAP Benefits (food assistance)' edge='start' name='hasSNAPBenefits' value={child.hasSNAPBenefits} setValue={(val) => { handleValueChange('hasSNAPBenefits', val) }} />
                <CheckBoxWithLabel label='Cash Assistance' edge='start' name='isOnCashAssistance' value={child.isOnCashAssistance} setValue={(val) => { handleValueChange('isOnCashAssistance', val) }} />
                <CheckBoxWithLabel label='SSI (Social Security Income)' edge='start' name='isOnSSI' value={child.isOnSSI} setValue={(val) => { handleValueChange('isOnSSI', val) }} />
              </Box>
            </Grid>
          )}

          <Grid item xs={6}>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', mb: '8px' }}>Address</Typography>
            {fieldError('addresses') != null && (
              <Typography variant='caption' color='error' display='block' data-testid='addressesError' sx={{ mb: '8px' }}>{fieldError('addresses')}</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {addresses.filter(a => child.locationIDs.includes(a.id)).map(a => {
                const isPrimary = child.primaryLocationID === a.id
                const pendingMod = pendingModifications.find(m => m.oldLocationID === a.id)
                const displayAddress = pendingMod == null
                  ? a
                  : { ...a, address: pendingMod.address, apartment: pendingMod.apartment, city: pendingMod.city, state: pendingMod.state, zipCode: pendingMod.zipCode, isHomeless: pendingMod.isHomeless, districtID: pendingMod.districtID ?? a.districtID }
                if (!isNew.current && modifyingAddress?.id === a.id) {
                  return (
                    <ChangeAddress
                      key={a.id}
                      inline
                      open={true}
                      title={isPrimary ? 'Modify Primary Address' : 'Modify Address'}
                      subjectLabel={childName(child)}
                      subjectId={child.id}
                      hideSharedChoice={!modifyingAddress.sharedWithOthers}
                      sharedChildren={modifyingAddress.sharedChildren}
                      showDistrict
                      isdID={isdID}
                      initialLocation={{ id: displayAddress.id, address: displayAddress.address, apartment: displayAddress.apartment, city: displayAddress.city, state: displayAddress.state, zipCode: displayAddress.zipCode, isHomeless: displayAddress.isHomeless, districtID: displayAddress.districtID ?? undefined, note: '' }}
                      onClose={() => { setModifyingAddress(null) }}
                      onSubmit={(newLocation, changeType, selectedMembers) => { handleModifyAddressSubmit(newLocation, changeType, selectedMembers) }}
                    />
                  )
                }
                return (
                  <AddressCard
                    key={a.id}
                    address={displayAddress}
                    isPrimary={isPrimary}
                    onSetPrimary={() => { handleSetPrimaryAddress(a.id) }}
                    onModify={isNew.current ? undefined : () => { startModifyingAddress(a) }}
                    onRemove={child.primaryLocationID === a.id ? undefined : () => { handleRemoveAddress(a.id) }}
                  />
                )
              })}
              {pendingNewAddresses.map(p => {
                const isPrimary = child.primaryLocationID === p.tempId
                if (modifyingPendingAddress?.tempId === p.tempId) {
                  return (
                    <ChangeAddress
                      key={p.tempId}
                      inline
                      open={true}
                      title={isPrimary ? 'Modify Primary Address' : 'Modify Address'}
                      hideSharedChoice={true}
                      showDistrict
                      isdID={isdID}
                      initialLocation={{ id: p.tempId, address: p.address, apartment: p.apartment, city: p.city, state: p.state, zipCode: p.zipCode, isHomeless: p.isHomeless, districtID: p.districtID, note: '' }}
                      onClose={() => { setModifyingPendingAddress(null) }}
                      onSubmit={(newLocation) => { handleModifyPendingAddressSubmit(newLocation) }}
                    />
                  )
                }
                return (
                  <AddressCard
                    key={p.tempId}
                    address={{ id: p.tempId, address: p.address, apartment: p.apartment, city: p.city, state: p.state, zipCode: p.zipCode, isHomeless: p.isHomeless }}
                    isPrimary={isPrimary}
                    onSetPrimary={() => { handleSetPrimaryAddress(p.tempId) }}
                    onModify={() => { startModifyingPendingAddress(p) }}
                    onRemove={isPrimary ? undefined : () => { handleRemoveAddress(p.tempId) }}
                  />
                )
              })}
              {addingAddress && (
                // a blank copy of the inline editor used to modify an address
                <ChangeAddress
                  inline
                  open={true}
                  title='Add Address'
                  hideSharedChoice={true}
                  showDistrict
                  isdID={isdID}
                  onClose={() => { setAddingAddress(false) }}
                  onSubmit={(newLocation) => { handleAddAddressSubmit(newLocation) }}
                />
              )}
            </Box>
            {!addingAddress && (
              <Typography
                onClick={startAddingAddress}
                data-testid='addAnotherAddress'
                sx={{ fontWeight: 600, fontSize: '16px', color: '#222024', textDecoration: 'underline', cursor: 'pointer', textAlign: 'center', mt: '12px' }}
              >
                +Add Another Address
              </Typography>
            )}
          </Grid>

        </Grid>

        <Box sx={{ mt: '16px', mb: '16px' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '18px', mb: '16px' }}>Caregiver Relationships</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {relationships.filter(r => r.id !== null).map(r => (
              modifyingRelationship?.caregiverID === r.caregiverID
                ? <RelationshipEdit
                    isChild={false}
                    key={r.caregiverID}
                    relationship={modifyingRelationship}
                    childName={childName(child)}
                    errors={relationshipErrors}
                    onCancel={() => { setModifyingRelationship(null); setRelationshipErrors([]) }}
                    onSubmit={(data) => { handleRelationshipSubmit(data) }}
                    onDelete={(modifyingRelationship.id ?? 0) < 0 && r.isSelf !== true ? () => { handleDeleteRelationship(r.caregiverID) } : undefined}
                />
                : <RelationshipCard
                    key={r.caregiverID}
                    relationship={r}
                    childName={childName(child)}
                    onModify={() => { setModifyingRelationship(r) }}
                />
            ))}
          </Box>
          {modifyingRelationship != null && modifyingRelationship.id === null && (
            <Box sx={{ mt: '12px' }}>
              <RelationshipEdit
                isChild={false}
                key={modifyingRelationship.caregiverID}
                relationship={modifyingRelationship}
                childName={childName(child)}
                errors={relationshipErrors}
                candidates={availableRelationships}
                onSwitchCandidate={(candidate) => { setModifyingRelationship(candidate) }}
                onCancel={() => { setModifyingRelationship(null); setRelationshipErrors([]) }}
                onSubmit={(data) => { handleRelationshipSubmit(data) }}
              />
            </Box>
          )}
          {availableRelationships.length > 0 && !(modifyingRelationship != null && modifyingRelationship.id === null) && (
            <Typography
              onClick={() => {
                setModifyingRelationship(availableRelationships.length === 1
                  ? availableRelationships[0]
                  : { ...availableRelationships[0], caregiverID: 0, caregiverName: '' })
              }}
              data-testid='addRelationship'
              sx={{ fontWeight: 600, fontSize: '16px', color: '#222024', textDecoration: 'underline', cursor: 'pointer', textAlign: 'center', mt: '12px' }}
            >
              {`+Add Pending Relationship (${availableRelationships.length})`}
            </Typography>
          )}
        </Box>

        <DialogActions sx={{ justifyContent: 'space-between', gap: '8px' }}>
          <Button
            data-testid='cancelButton'
            variant='outlined'
            onClick={returnToDetails}
            sx={{ flex: 1, borderRadius: '50px', borderColor: '#969696', color: '#222024', textTransform: 'none', py: '16px' }}
          >
            Cancel
          </Button>

          <Tooltip title={hasUnsavedSubEdits ? 'Finish or cancel the address/relationship changes you\'re editing before saving' : ''}>
            <span style={{ flex: 1 }}>
              <Button
                data-testid='updateButton'
                onClick={handleSubmit}
                variant='contained'
                fullWidth
                disabled={submitting || hasUnsavedSubEdits}
                sx={{ borderRadius: '50px', backgroundColor: '#0E8476', textTransform: 'none', py: '16px', '&:hover': { backgroundColor: '#0b6c60' } }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Box>
    </Container >
  }
}
